import { useMemo } from "react";

import {
  AuditEntry,
  GoLiveAttempt,
  JourneyStep,
  OnboardingState,
  SetupFee,
  StateTransition,
} from "../types/onboarding";
import { buildJourney } from "../utils/prospectJourney";
import { nextActionFor } from "../utils/onboardingStates";
import { actorLabel } from "../utils/actors";
import { isCustomerPromoted, SETUP_FEE_NEXT_ACTION } from "../utils/setupFee";

// The backend writes this audit field on every go-live call, successful email
// or not.
const GO_LIVE_FIELD = "GO_LIVE_TRIGGERED";

interface UseProspectJourneyParams {
  currentState: OnboardingState | null | undefined;
  transitions: StateTransition[] | null | undefined;
  moreInfoNeeded?: boolean;
  audit: AuditEntry[] | null | undefined;
  // The setup fee (CWA-196), from `useProspectSetupFee`. Null until promotion.
  setupFee?: SetupFee | null;
}

interface UseProspectJourney {
  steps: JourneyStep[];
  nextAction: string | null;
  lastGoLiveAttempt: GoLiveAttempt | null;
}

/**
 * Two derived views of a prospect, both computed from data `useProspectDetail`
 * already holds — this hook issues no request of its own.
 *
 * - `steps` / `nextAction`: the onboarding journey, including the steps still
 *   outstanding. Built by the pure `buildJourney` so the branching is testable
 *   without rendering.
 * - `lastGoLiveAttempt`: the newest go-live audit entry. Deliberately called an
 *   *attempt*: the backend records it whether or not the confirmation email was
 *   delivered, and exposes its own email status on no response we can read.
 */
const useProspectJourney = ({
  currentState,
  transitions,
  moreInfoNeeded = false,
  audit,
  setupFee = null,
}: UseProspectJourneyParams): UseProspectJourney => {
  const steps = useMemo(
    () =>
      currentState
        ? buildJourney({
            currentState,
            transitions: transitions ?? [],
            moreInfoNeeded,
            setupFee,
          })
        : [],
    [currentState, transitions, moreInfoNeeded, setupFee]
  );

  // An open setup fee comes first once the prospect is promoted, as in the
  // header's next step: it never blocks the stage, but it is what is owed now.
  const feeOpen = setupFee?.status === "OPEN";
  const nextAction = useMemo(() => {
    if (!currentState) return null;
    if (feeOpen && isCustomerPromoted(currentState)) return SETUP_FEE_NEXT_ACTION;
    return nextActionFor(currentState);
  }, [currentState, feeOpen]);

  const lastGoLiveAttempt = useMemo<GoLiveAttempt | null>(() => {
    const entries = (audit ?? []).filter((e) => e.field_name === GO_LIVE_FIELD);
    if (!entries.length) return null;

    const newest = entries.reduce((latest, entry) =>
      new Date(entry.changed_at).getTime() > new Date(latest.changed_at).getTime()
        ? entry
        : latest
    );

    return {
      attempted_at: newest.changed_at,
      actor: actorLabel(newest.actor_id) || newest.actor_role,
      station_ids: (newest.new_value ?? "")
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean),
    };
  }, [audit]);

  return { steps, nextAction, lastGoLiveAttempt };
};

export default useProspectJourney;
